import { useEffect, useState } from 'react'
import { SHARE_ORIGINAL_EVENT, readShareOriginal, writeShareOriginal } from '../links/shareOriginal'
import { shareOriginalApplies, swapShareText, type ShareOriginalRecord } from '../links/shareOriginalRecord'
import { Icon } from './Icon'

interface ShareTidyStripProps {
  noteId: string
  body: string
  /** Replaces the note body; the caller owns draft state, history and saving. */
  onBodyChange: (body: string) => void
}

/**
 * A quiet line under a shared note that says its packaging was trimmed and brings the original back.
 * It shows only while the note still holds the share's text; once that text is edited away it goes.
 */
export function ShareTidyStrip({ noteId, body, onBodyChange }: ShareTidyStripProps) {
  const [record, setRecord] = useState<ShareOriginalRecord | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    let current = true
    const load = () => { void readShareOriginal(noteId).then((next) => { if (current) setRecord(next) }).catch(() => undefined) }
    load()
    const onWritten = (event: Event) => { if ((event as CustomEvent<{ noteId: string }>).detail?.noteId === noteId) load() }
    window.addEventListener(SHARE_ORIGINAL_EVENT, onWritten)
    return () => { current = false; window.removeEventListener(SHARE_ORIGINAL_EVENT, onWritten) }
  }, [noteId])

  if (!record || !shareOriginalApplies(body, record)) return null
  const restored = record.mode === 'original'
  const swap = (to: 'tidy' | 'original') => {
    const next = swapShareText(body, record, to)
    if (next === null) return
    const updated = { ...record, mode: to }
    setRecord(updated)
    setOpen(false)
    onBodyChange(next)
    void writeShareOriginal(noteId, updated).catch(() => undefined)
  }
  return <div className="share-tidy" data-mode={record.mode}>
    <div className="share-tidy__line">
      <Icon name={restored ? 'undo' : 'scissors'} />
      <span>{restored ? 'Original share restored' : 'Tidied share text'}</span>
      {restored
        ? <button type="button" className="btn btn-ghost share-tidy__action" onClick={() => swap('tidy')}>Tidy again</button>
        : <button type="button" className="btn btn-ghost share-tidy__action" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          {open ? 'Hide original' : 'Show original'}
        </button>}
    </div>
    {open && !restored ? <div className="share-tidy__original">
      <p>{record.original}</p>
      <button type="button" className="btn btn-primary" onClick={() => swap('original')}>Restore original</button>
    </div> : null}
  </div>
}
